import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import Button from '../ui/button';
import Input from '../ui/input';
import {
  Search,
  Filter,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import modelComparisonService from '../../services/modelComparisonService';
import ModelComparisonCard from './ModelComparisonCard';
import ModelComparisonForm from './ModelComparisonForm';
import ModelComparisonStats from './ModelComparisonStats';

const ModelComparisonDashboard = () => {
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingComparison, setEditingComparison] = useState(null);
  const [stats, setStats] = useState({
    total_comparisons: 0,
    completed_comparisons: 0,
    running_comparisons: 0,
    failed_comparisons: 0,
    success_rate: 0
  });

  const location = useLocation();

  const loadComparisons = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await modelComparisonService.getComparisons(params);
      // Handle paginated response structure (response.data.data) or flat array (response.data)
      let comparisonsData = response.data?.data || response.data || [];

      // Ensure comparisonsData is an array
      if (!Array.isArray(comparisonsData)) {
        console.warn('Expected comparisonsData to be an array, but got:', typeof comparisonsData);
        comparisonsData = [];
      }

      setComparisons(comparisonsData);
    } catch (error) {
      console.error('Failed to load comparisons:', error);
      toast.error(error.message || 'Failed to load comparisons');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus]);

  const loadStats = useCallback(async () => {
    try {
      const response = await modelComparisonService.getStats();
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    await Promise.all([loadComparisons(), loadStats()]);
  }, [loadComparisons, loadStats]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sync filterStatus from URL query (?status=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const allowed = ['all', 'active', 'running', 'completed', 'paused', 'failed'];
    if (statusParam && allowed.includes(statusParam)) {
      setFilterStatus(statusParam);
    } else {
      // Reset to all when status param is missing or invalid
      setFilterStatus('all');
    }
  }, [location.search]);

  const handleCreateComparison = async (data) => {
    try {
      const response = await modelComparisonService.createComparison(data);
      toast.success(response.message || 'Comparison created successfully');
      setShowCreateModal(false);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create comparison');
      throw error;
    }
  };

  const handleUpdateComparison = async (id, data) => {
    try {
      const response = await modelComparisonService.updateComparison(id, data);
      toast.success(response.message || 'Comparison updated successfully');
      setEditingComparison(null);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update comparison');
      throw error;
    }
  };

  const handleDeleteComparison = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      const response = await modelComparisonService.deleteComparison(id);
      toast.success(response.message || 'Comparison deleted successfully');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete comparison');
    }
  };

  const handleExecuteComparison = async (id) => {
    try {
      const response = await modelComparisonService.executeComparison(id);
      toast.success(response.message || 'Comparison execution started');
      await loadComparisons();
    } catch (error) {
      toast.error(error.message || 'Failed to execute comparison');
    }
  };

  const handleCloneComparison = async (id, originalName) => {
    const name = prompt(`Enter name for cloned comparison:`, `${originalName} (Copy)`);
    if (!name) return;

    try {
      const response = await modelComparisonService.cloneComparison(id, name);
      toast.success(response.message || 'Comparison cloned successfully');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to clone comparison');
    }
  };

  const filteredComparisons = Array.isArray(comparisons) ? comparisons.filter(comparison => {
    const matchesSearch = comparison.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (comparison.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || comparison.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-8 w-8" />
            Model Comparison Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Compare performance across different LLM models and providers
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <ModelComparisonStats stats={stats} />

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search comparisons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="paused">Paused</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Comparisons List */}
      <Card>
        <CardHeader>
          <CardTitle>Model Comparisons</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Loading comparisons...</p>
            </div>
          ) : filteredComparisons.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {searchTerm || filterStatus !== 'all' ? 'No matching comparisons' : 'No comparisons yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by creating your first model comparison'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredComparisons.map((comparison) => (
                <ModelComparisonCard
                  key={comparison.id}
                  comparison={comparison}
                  onEdit={(comp) => setEditingComparison(comp)}
                  onDelete={handleDeleteComparison}
                  onExecute={handleExecuteComparison}
                  onClone={handleCloneComparison}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingComparison) && (
        <ModelComparisonForm
          comparison={editingComparison}
          onSave={editingComparison ?
            (data) => handleUpdateComparison(editingComparison.id, data) :
            handleCreateComparison
          }
          onCancel={() => {
            setShowCreateModal(false);
            setEditingComparison(null);
          }}
        />
      )}
    </div>
  );
};

export default ModelComparisonDashboard;
